import { Injectable, inject } from '@angular/core';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { map, of, switchMap, withLatestFrom } from 'rxjs';
import { CommentActions } from '.';
import { DeleteCommentRequestPayload, LoadCommentsPagePayload } from './comment.actions';
import { CommentDto } from '../../models/comment.model';
import { DsmApiResponseDataDto } from '../../models/dsm-api-call.model';
import { AggregateTypes } from '../../models/store.model';
import { CommentService } from '../../services/comments.service';
import { AccountReducers, AccountSelectors } from '../account';

@Injectable()
export class CommentEffects {
  private _accountStore = inject<Store<AccountReducers.AccountState>>(Store);
  private _commentService = inject(CommentService);
  private _actions$ = inject(Actions);

  /**
   *
   */
  loadCommentsPage$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CommentActions.LOAD_COMMENTS_PAGE),
      map((action: CommentActions.LoadCommentsPage) => action?.payload),
      switchMap((payload: LoadCommentsPagePayload) => {
        return this._commentService.loadComments(payload?.aggregateId, payload?.aggregateType, payload?.limit, payload?.offset).pipe(
          switchMap((resp: DsmApiResponseDataDto) => {
            const actions: Action[] = [new CommentActions.LoadCommentsPageSuccessful({ request: payload, response: resp })];

            if (!!payload?.autoLoadNextPage && resp.data?.length === payload?.limit) {
              // if next page is possible and autoload is active
              const nextPayload: LoadCommentsPagePayload = Object.assign({}, payload);
              nextPayload.offset = payload?.offset + payload?.limit;
              actions.push(new CommentActions.LoadCommentsPage(nextPayload));
            }
            return actions;
          })
        );
      })
    )
  );

  /**
   *
   */
  writeComment$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CommentActions.WRITE_COMMENT),
      map((action: CommentActions.WriteComment) => action?.payload),
      withLatestFrom(this._accountStore.select(AccountSelectors.getUserProfile)),
      switchMap(([payload, userProfile]) => {
        if (!payload?.comment?.referencedAggregateId || !payload?.comment?.referencedAggregateType)
          return of(new CommentActions.WriteCommentFailed({ request: payload, error: Error('Missing valid referencedAggregate') }));

        const comment: CommentDto = Object.assign({}, payload?.comment);

        return (comment?.aggregateId ? this._commentService.updateComment(comment) : this._commentService.addComment(comment)).pipe(
          map((resp: DsmApiResponseDataDto) => {
            if (!comment?.aggregateType) comment.aggregateType = AggregateTypes.Comment;
            if (!comment?.createdAt) comment.createdAt = new Date();
            if (!comment?.createdBy) comment.createdBy = userProfile.userId;
            comment.updatedAt = new Date();
            if (!comment?.updatedBy) comment.updatedBy = userProfile.userId;
            if (!comment?.aggregateId) comment.aggregateId = resp?.data?.aggregateId;

            return new CommentActions.WriteCommentSuccessful({ request: payload, response: comment });
          })
        );
      })
    )
  );

  /**
   *
   */
  deleteComment$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CommentActions.DELETE_COMMENT),
      map((action: CommentActions.DeleteComment) => action?.payload),
      switchMap((payload: DeleteCommentRequestPayload) => {
        if (!payload?.referencedAggregateId || !payload?.referencedAggregateType || !payload?.aggregateId)
          return of(new CommentActions.DeleteCommentFailed({ request: payload, error: Error('Missing valid referencedAggregate') }));

        return this._commentService.deleteComment(payload?.referencedAggregateType, payload?.referencedAggregateId, payload?.aggregateId).pipe(
          map((resp: DsmApiResponseDataDto) => {
            return new CommentActions.DeleteCommentSuccessful({ request: payload, response: resp });
          })
        );
      })
    )
  );
}
